import { Fragment, type Node, type ResolvedPos, type Schema, Slice } from 'prosemirror-model'
import {
  type Command,
  type EditorState,
  Plugin,
  TextSelection,
  type Transaction,
} from 'prosemirror-state'

import { canvasMeasure, type MeasuredView } from './measure'
import { allowsBreaks, reflow } from './reflow'

const normalizeNewlines = (text: string) => text.replace(/\r\n?/g, '\n')

const PARAGRAPH_BREAK = /\n[ \t]*\n/

// A word hyphenated at the line end: `Ver-⏎trag` → `Ver-trag`
// - The hyphen stays: it may belong to the word (`Max-⏎Planck`), only the editor can tell.
const HYPHENATED = /(\p{L})-[ \t]*\n[ \t]*(?=\p{L})/gu

// Single line breaks are wrap artefacts of the source (PDFs); blank lines stay paragraphs
// - Spaces at the edges stay: pasted mid-sentence they keep the words apart.
export const joinLines = (text: string) =>
  normalizeNewlines(text)
    .split(PARAGRAPH_BREAK)
    .map((paragraph) =>
      paragraph
        .replace(/^[ \t]*\n\s*|\s*\n[ \t]*$/g, '')
        .replace(HYPHENATED, '$1-')
        .replace(/[ \t]*\n[ \t]*/g, ' ')
    )
    .filter((paragraph) => paragraph.trim())
    .join('\n\n')

// Blank lines become paragraphs, single line breaks hard breaks (kept on Shift-paste, e.g. poems)
export const parsePastedText = (text: string, $context: ResolvedPos, schema: Schema) => {
  const marks = $context.marks()
  const paragraphs = normalizeNewlines(text)
    .split(PARAGRAPH_BREAK)
    .map((paragraph) => {
      const content = paragraph.split('\n').flatMap((line, i) => [
        ...(i > 0 ? [schema.nodes.hard_break.create()] : []),
        ...(line ? [schema.text(line, marks)] : []),
      ])
      return schema.nodes.paragraph.create(null, content)
    })
  return new Slice(Fragment.from(paragraphs), 1, 1)
}

// Pasted HTML carries its <br>s as hard breaks; outside Shift-paste they count as spaces
const breaksToSpaces = (fragment: Fragment): Fragment => {
  let result = Fragment.empty
  fragment.forEach((child) => {
    const node: Node =
      child.type.name === 'hard_break'
        ? child.type.schema.text(' ', child.marks)
        : child.isLeaf
          ? child
          : child.copy(breaksToSpaces(child.content))
    // append() joins adjacent text nodes with equal marks
    result = result.append(Fragment.from(node))
  })
  return result
}

export const reflowCommand =
  (lineWidth: number): Command =>
  (state, dispatch, view) => {
    if (!view || !state.selection.$from.parent.isTextblock) return false
    if (dispatch) {
      const { from, to } = state.selection
      const tr = reflow(state.tr, from, to, canvasMeasure(view, lineWidth))
      if (tr.docChanged) dispatch(tr.scrollIntoView())
    }
    return true
  }

// The selection widened over the spaces next to it, which would start or end a line
const withSpacesAround = (state: EditorState) => {
  const { $from, $to } = state.selection
  if (!$from.sameParent($to)) return state.selection
  const block = $from.parent
  // One character per inline node, so string offsets are parent offsets
  const text = block.textBetween(0, block.content.size, undefined, '\ufffc')
  let from = $from.parentOffset
  let to = $to.parentOffset
  while (text[from - 1] === ' ') from--
  while (text[to] === ' ') to++
  return TextSelection.create(state.doc, $from.start() + from, $from.start() + to)
}

// Mod-Shift-Enter: break here, then re-break the rest of the paragraph around it
export const breakAndReflowCommand =
  (lineWidth: number): Command =>
  (state, dispatch, view) => {
    if (!view || !allowsBreaks(state.selection.$from.parent)) return false
    if (dispatch) {
      const tr = state.tr
        .setSelection(withSpacesAround(state))
        .replaceSelectionWith(state.schema.nodes.hard_break.create())
      const { from } = tr.selection
      dispatch(reflow(tr, from, from, canvasMeasure(view, lineWidth, tr.mapping)).scrollIntoView())
    }
    return true
  }

type PasteView = MeasuredView & {
  state: EditorState
  dispatch: (tr: Transaction) => void
}

// Inserts the slice, then re-breaks from the pasted line on
// - Here, not in transformPasted: a drop runs that too, but is never re-broken.
const handlePaste = (view: PasteView, slice: Slice, lineWidth: number) => {
  const single = slice.openStart === 0 && slice.openEnd === 0 && slice.content.childCount === 1
  if (!slice.size || single) return false
  const content = breaksToSpaces(slice.content)
  const tr = view.state.tr.replaceSelection(new Slice(content, slice.openStart, slice.openEnd))
  const from = tr.mapping.map(view.state.selection.from, -1)
  reflow(tr, from, tr.selection.to, canvasMeasure(view, lineWidth, tr.mapping))
  view.dispatch(tr.scrollIntoView().setMeta('paste', true).setMeta('uiEvent', 'paste'))
  return true
}

// Fits text to the field's guide (`lineWidth` in em) on paste and on demand
export const lineBreaksPlugin = (lineWidth: number) => {
  const breakAndReflow = breakAndReflowCommand(lineWidth)
  // Shift-paste (poems): ProseMirror reads plain text only; its lines stay, nothing is re-broken
  // - Only the text parser learns about Shift, so it hands the flag on to handlePaste.
  let plainPaste = false
  // Drops run the text transforms too, but are never re-broken, so their lines stay
  let pasting = false
  return new Plugin({
    props: {
      handleDOMEvents: {
        paste: () => {
          pasting = true
          return false
        },
        drop: () => {
          pasting = false
          return false
        },
      },
      transformPastedText: (text, plain) => (pasting && !plain ? joinLines(text) : text),
      clipboardTextParser: (text, $context, plain, view) => {
        plainPaste = plain
        return parsePastedText(text, $context, view.state.schema)
      },
      // Hard breaks as newlines, so a Shift-paste elsewhere keeps the lines
      clipboardTextSerializer: (slice) =>
        slice.content.textBetween(0, slice.content.size, '\n\n', (leaf) =>
          leaf.type.name === 'hard_break' ? '\n' : (leaf.type.spec.leafText?.(leaf) ?? '')
        ),
      handlePaste: (view, _event, slice) => {
        const plain = plainPaste
        plainPaste = false
        return !plain && handlePaste(view, slice, lineWidth)
      },
      handleKeyDown: (view, event) =>
        event.key === 'Enter' &&
        event.shiftKey &&
        (event.metaKey || event.ctrlKey) &&
        breakAndReflow(view.state, view.dispatch, view),
    },
  })
}
